import {
  Supertype,
  supertypeClass,
  property,
  remote,
  Remoteable,
  Persistable,
  amorphicStatic,
} from '@haventech/amorphic';
import {
  IsomorphicPersistent,
  Persistent,
  IsomorphicQuery,
  Query,
  Isomorphic,
  Serializable,
  AppController,
  SubController,
  Everything,
} from '@hit/amorphic-types';
import { Expense } from './Expense';
import { CashFlow } from './CashFlow';

@supertypeClass({ toClient: true, toServer: true })
export class ReplacementIncome extends Expense {
  @property()
  stage: number = 1;

  @property()
  amount: number;

  @property({
    rules: ['percent'],
    values: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1, 1.1, 1.2, 1.3, 1.4, 1.5],
    descriptions: {
      '0': 'None',
      '1': '100%',
      '0.1': '10%',
      '0.2': '20%',
      '0.3': '30%',
      '0.4': '40%',
      '0.5': '50%',
      '0.6': '60%',
      '0.7': '70%',
      '0.8': '80%',
      '0.9': '90%',
      '1.1': '110%',
      '1.2': '120%',
      '1.3': '130%',
      '1.4': '140%',
      '1.5': '150%',
    },
  })
  covered: number = 1;

  @property({ isLocal: true, getType: () => Number })
  coveredOrder: Array<number> = [1, 0.9, 0.8, 0.7, 0.6, 0.5, 0.4, 0.3, 0.2, 0.1, 0];

  constructor() {
    super();
  }

  static new(capitalNeeds, stage, ratio, title) {
    return new ReplacementIncome().init(capitalNeeds, stage, ratio, title);
  }

  init(capitalNeeds, stage, ratio, title) {
    super.init(capitalNeeds ? capitalNeeds.customer : null, 0, 'replacement', title);

    this.stage = stage;
    this.capitalNeeds = capitalNeeds;
    this.covered = ratio;
    return this;
  }

  isActive() {
    return this.covered > 0;
  }

  getStartingFlow(deceased) {
    const flow = super.getStartingFlow(deceased);
    const deceasedIncomeIx = deceased === this.customer.primaryCustomer ? 0 : 1;
    const survivorIncomeIx = deceasedIncomeIx ? 0 : 1;
    const deceasedIncome = this.capitalNeeds.earnedIncome[deceasedIncomeIx].amount * 0.66;
    const survivorIncome = this.capitalNeeds.earnedIncome[survivorIncomeIx].amount * 0.66;

    if (this.customer.assumptions.percentIsOfBothIncomes) {
      flow.amount = (deceasedIncome + survivorIncome) * this.covered;
    } else {
      flow.amount = deceasedIncome * this.covered + survivorIncome;
    }

    return flow;
  }

  getAmount(flow, year, deceased, yearOfDeath) {
    if (year < yearOfDeath) {
      return 0;
    }

    let stage = 0;

    // default nothing
    if (this.capitalNeeds.children.length > 0) {
      if (year < this.capitalNeeds.getLeaveTheNestYear()) {
        stage = 1;
      } else if (year < this.capitalNeeds.getRetirementYear(deceased)) {
        stage = this.customer.profile.relationship === 'single' ? 0 : 2;
      } else {
        stage = 3;
      }
    } else {
      if (year < this.capitalNeeds.getRetirementYear(deceased)) {
        stage = 2;
      } else {
        stage = 3;
      }
    }

    if (stage === this.stage) {
      return 0 - flow.amount;
    } else {
      return 0;
    }
  }

  getDescription() {
    const stages = ['', 'w/kids', 'after', 'retired'];

    return 'Need ' + stages[this.stage];
  }
}
